<template>
  <validation-observer v-slot="{ invalid }" ref="form" class="form mx-auto">
    <b-form @submit.prevent="handleSubmit">
      <slot name="header">
        <h4 class="mb-4">{{ formData.id ? "Update" : "Add" }} Company</h4>
      </slot>

      <base-input
        v-model="formData.name"
        rules="required"
        name="Company Name"
        label="Company Name"
      ></base-input>

      <base-input
        v-model="formData.email_domain"
        rules="email_domain"
        name="Email Domain"
        label="Email Domain"
      ></base-input>

      <b-form-group label="Role" label-size="lg">
        <validation-provider rules="required" vid="role" v-slot="{ errors }" name="Role">
          <FormRoleDropdown v-model="formData.role_id" />
          <InvalidFeedback :messages="errors" />
        </validation-provider>
      </b-form-group>

      <base-input
        v-model="formData.role_value"
        rules="required"
        name="Role Value"
        label="Role Value"
      ></base-input>

      <base-select
        v-model="formData.default_report_id"
        rules="required"
        :options="reportOptions"
        name="Default Report"
        label="Default Report"
        placeholder="Select Default Report"
      ></base-select>

      <base-select
        v-model="formData.fcp_role"
        :options="fcpRoles"
        name="REC Role"
        label="REC Role"
        placeholder="Select REC Role"
      ></base-select>

      <b-form-group label="Address" label-size="lg">
        <b-form-textarea
          v-model="formData.address"
          name="Address"
          rows="3"
          max-rows="6"
          class="form-control"
        />
      </b-form-group>

      <base-input
        v-model="formData.filevine_id"
        name="FileVine Project Email"
        label="FileVine Project Email"
      ></base-input>

      <div class="mt-4 d-flex justify-content-between">
        <slot name="return"></slot>
        <slot name="submit">
          <base-button
            :disabled="invalid"
            :loading="formProcessing"
            type="primary"
            native-type="submit"
            size="md"
            class="form-btn font-weight-normal"
          >
            <slot name="submit-text">Add Company</slot>
          </base-button>
        </slot>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import Vue from "vue";
import { createVuexHelpers } from "vue2-helpers";
import {
  defineComponent,
  computed,
  ref,
  reactive,
  watch,
  onMounted,
} from "@vue/composition-api";

import Companies from "@services/Companies";
import Reports from "@services/Reports";

import FormRoleDropdown from "@components/FormRoleDropdown";
import InvalidFeedback from "@components/InvalidFeedback";

export default defineComponent({
  name: "FormCompany",
  components: {
    FormRoleDropdown,
    InvalidFeedback,
  },
  props: {
    action: {
      type: String,
      default: "create",
      required: true,
    },
  },
  setup(props) {
    const fcpRoles = [
      {
        text: "Admin",
        value: "Admin",
      },
      {
        text: "User",
        value: "User",
      },
      {
        text: "View",
        value: "View",
      },
    ];

    const { useGetters, useMutations } = createVuexHelpers();
    const { company, user } = useGetters(["company", "user"]);
    const { CREATE_COMPANY, READ_COMPANY, UPDATE_COMPANY } = useMutations([
      "CREATE_COMPANY",
      "READ_COMPANY",
      "UPDATE_COMPANY",
    ]);

    const formProcessing = ref(false);
    const reportOptions = ref([]);

    const formData = reactive({});

    const isCreate = computed(() => "create" === props.action);

    const getReports = async () => {
      const { data } = await Reports.getReports(
        isCreate.value ? user.value.id : company.value.id,
        isCreate.value ? "superuser" : "Corporate Admin"
      );

      reportOptions.value = data.data.data.map((item) => ({
        text: item.name,
        value: item.report_id || item.id,
      }));
    };

    const handleSubmit = async () => {
      let response;

      formProcessing.value = true;

      if (isCreate.value) {
        response = await Companies.createCompany(formData);

        CREATE_COMPANY(response.data.data);
      } else {
        response = await Companies.updateCompany(formData.id, formData);

        UPDATE_COMPANY(response.data.data);
      }

      READ_COMPANY(response.data.data);

      formProcessing.value = false;

      if (200 === response.data.status)
        Vue.prototype.$bus.$emit("company-form-success", response.data.data.data);

      Vue.prototype.$toasted.show(response.data.data.message);
    };

    watch(
      () => formProcessing.value,
      (newFormProcessing) =>
        Vue.prototype.$bus.$emit("company-form-processing", newFormProcessing)
    );

    watch(
      () => company.value,
      (newCompany) => {
        formData.id = newCompany.id;
        formData.name = newCompany.name;
        formData.email_domain = newCompany.email_domain;
        formData.role_id = newCompany.role_id;
        formData.role_value = newCompany.role_value;
        formData.status = "Active";
        formData.default_report_id = newCompany.default_report_id;
        formData.fcp_role = newCompany.fcp_role;
        formData.address = newCompany.address || ''
        formData.filevine_id = newCompany.filevine_id || ''
      },
      { deep: true, immediate: true }
    );

    onMounted(() => {
      getReports();

      Vue.prototype.$bus.trackEvent(
        "error-catched",
        () => (formProcessing.value = false)
      );
    });

    return {
      fcpRoles,
      formProcessing,
      reportOptions,
      formData,
      handleSubmit,
    };
  },
});
</script>
